import React, { useCallback } from "react";
import classNames from "classnames";
import { SortIcon, SpinnerIcon } from "@icons";
import { Tooltip } from "../Tooltip/Tooltip";
import { BreakableText } from "../BreakableText/BreakableText";
import styles from "./SortableHeader.module.scss";

interface SortableHeaderProps<T extends string> {
    text: string;
    sort?: T;
    onChange?: (sort: T) => void;
    // When provided, the sort cycle gains a third "unsorted" step: the click
    // after the final sort direction calls this to clear sorting instead of
    // looping back. Opt-in so screens that can't represent an unsorted state
    // keep the original two-state (asc ↔ desc) toggle.
    onSortRemove?: () => void;
    asc: T;
    desc: T;
    defaultOrder: "asc" | "desc";
    className?: string;
    // When true, the sort arrow is swapped for a spinner. Intended to be
    // passed only while the data for the currently active sort column is
    // being (re)fetched, e.g. right after the user clicks this header.
    isLoading?: boolean;
}

export const SortableHeader = <T extends string = string>({
    sort,
    asc,
    desc,
    onChange,
    onSortRemove,
    defaultOrder,
    text,
    className,
    isLoading,
}: SortableHeaderProps<T>) => {
    const handleClick = useCallback(() => {
        // The first click sorts by the column's default direction; the second
        // flips it. With onSortRemove the third click clears the sort, then the
        // cycle repeats. Without it the third click loops back (legacy 2-state).
        const firstDirection = defaultOrder === "asc" ? asc : desc;
        const secondDirection = defaultOrder === "asc" ? desc : asc;
        if (sort !== asc && sort !== desc) {
            onChange?.(firstDirection);
            return;
        }
        if (sort === firstDirection) {
            onChange?.(secondDirection);
            return;
        }
        if (onSortRemove) {
            onSortRemove();
            return;
        }
        onChange?.(firstDirection);
    }, [asc, desc, onChange, onSortRemove, sort, defaultOrder]);

    if (!onChange) {
        return <BreakableText text={text} showTooltip />;
    }

    const tooltipText = (() => {
        const secondDirection = defaultOrder === "asc" ? desc : asc;
        // When removal is enabled, the click after the final sort direction
        // clears sorting.
        if (onSortRemove && sort === secondDirection) {
            return "Clear sort";
        }
        if (sort === asc) {
            return "Sort descending";
        }
        if (sort === desc) {
            return "Sort ascending";
        }
        // Unsorted: tooltip describes what the first click will do
        return defaultOrder === "desc" ? "Sort descending" : "Sort ascending";
    })();

    return (
        <Tooltip content={tooltipText}>
            <button
                type="button"
                className={classNames(styles.sortableHeader, className)}
                onClick={handleClick}
            >
                {/* showTooltip omitted: the outer Tooltip handles truncation/sort text */}
                <BreakableText text={text} />
                {(sort === asc || sort === desc) &&
                    (isLoading ? (
                        <SpinnerIcon className={styles.spinner} />
                    ) : (
                        <SortIcon
                            className={classNames(styles.arrow, {
                                [styles.asc]: sort === asc,
                                [styles.desc]: sort === desc,
                            })}
                        />
                    ))}
            </button>
        </Tooltip>
    );
};
